import { ulBrush01 } from '@faceup/icons/ulBrush01'
import { ulFile02 } from '@faceup/icons/ulFile02'
import { ulFolder } from '@faceup/icons/ulFolder'
import { ulInbox01 } from '@faceup/icons/ulInbox01'
import { ulPhoneCall01 } from '@faceup/icons/ulPhoneCall01'
import { ulPhoneOutgoing02 } from '@faceup/icons/ulPhoneOutgoing02'
import { ulPieChart01 } from '@faceup/icons/ulPieChart01'
import { ulSearchRefraction } from '@faceup/icons/ulSearchRefraction'
import { ulServer05 } from '@faceup/icons/ulServer05'
import { ulSettings01 } from '@faceup/icons/ulSettings01'
import { ulUsers02 } from '@faceup/icons/ulUsers02'
import { ulZap } from '@faceup/icons/ulZap'
import { defineMessages, useIntl, useLocalization } from '@faceup/localization'
import { useAccessRights, usePartner } from '@faceup/member'
import { ucLinking, ucMessageSettings } from '@faceup/ui'
import {
  canAddMoreMembers,
  canAddMoreReportingForms,
  Feature,
  Institution,
  Language,
  Plan,
} from '@faceup/utils'
import { ucClipboard } from '../../../customIcons/ucClipboard'
import { useInstitution } from '../../../providers'
import type { UpsellData } from '../upsellData'
import imageEmployeeManagementCs from './assets/upsell-employee-management-cs.png'
import imageEmployeeManagementEn from './assets/upsell-employee-management-en.png'
import imageInvestigationsCs from './assets/upsell-investigations-cs.png'
import imageInvestigationsEn from './assets/upsell-investigations-en.png'
import imageOrganizationStructureCs from './assets/upsell-organization-structure-cs.png'
import imageOrganizationStructureEn from './assets/upsell-organization-structure-en.png'
import imageReportsCs from './assets/upsell-reports-cs.png'
import imageStatisticsCs from './assets/upsell-statistics-cs.png'
import imageStatisticsEn from './assets/upsell-statistics-en.png'
import imageSurveysCs from './assets/upsell-surveys-cs.png'
import imageSurveysEn from './assets/upsell-surveys-en.png'
import imageWhitelabelCs from './assets/upsell-whitelabel-cs.png'
import imageWhitelabelEn from './assets/upsell-whitelabel-en.png'

const messages = {
  reports: defineMessages('institution.upsell.reports', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  statistics: defineMessages('institution.upsell.statistics', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  organisationStructure: defineMessages('institution.upsell.organisationStructure', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  categories: defineMessages('institution.upsell.categories', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  caseStatuses: defineMessages('institution.upsell.caseStatuses', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  formAppearance: defineMessages('institution.upsell.formAppearance', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  addChannelLiveHotline: defineMessages('institution.upsell.addChannelLiveHotline', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  addChannelAutomatedHotline: defineMessages('institution.upsell.addChannelAutomatedHotline', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  addChannelAiHotline: defineMessages('institution.upsell.addChannelAiHotline', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  addChannelForm: defineMessages('institution.upsell.addChannelForm', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  addMember: defineMessages('institution.upsell.addMember', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  automaticDueDate: defineMessages('institution.upsell.automaticDueDate', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  endToEndEncryption: defineMessages('institution.upsell.endToEndEncryption', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  schoolEnableAccessKey: defineMessages('institution.upsell.schoolEnableAccessKey', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  materials: defineMessages('institution.upsell.materials', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  sso: defineMessages('institution.upsell.sso', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  webhooks: defineMessages('institution.upsell.webhooks', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  apiKeys: defineMessages('institution.upsell.apiKeys', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  whitelabel: defineMessages('institution.upsell.whitelabel', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  schoolEditUserPermissions: defineMessages('institution.upsell.schoolEditUserPermissions', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  integrations: defineMessages('institution.upsell.integrations', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  surveys: defineMessages('institution.upsell.surveys', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  investigations: defineMessages('institution.upsell.investigations', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  ipAllowlist: defineMessages('institution.upsell.ipAllowlist', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
  employeeManagement: defineMessages('institution.upsell.employeeManagement', [
    { id: 'title', defaultMessage: '' },
    { id: 'features', defaultMessage: '' },
    { id: 'description', defaultMessage: '' },
  ] as const),
} satisfies Record<UpsellFor, Record<Messages, unknown>>

type Messages = 'title' | 'description' | 'features'

type UpsellFor =
  | 'reports'
  | 'statistics'
  | 'organisationStructure'
  | 'categories'
  | 'caseStatuses'
  | 'addChannelLiveHotline'
  | 'addChannelAutomatedHotline'
  | 'addChannelAiHotline'
  | 'addChannelForm'
  | 'addMember'
  | 'automaticDueDate'
  | 'formAppearance'
  | 'endToEndEncryption'
  | 'schoolEnableAccessKey'
  | 'schoolEditUserPermissions'
  | 'sso'
  | 'materials'
  | 'whitelabel'
  | 'surveys'
  | 'webhooks'
  | 'apiKeys'
  | 'integrations'
  | 'investigations'
  | 'ipAllowlist'
  | 'employeeManagement'

type Material = {
  id: string
  isAccessible: boolean
  downloadLink: string | null
}

export type UpsellReturnType = Record<Exclude<UpsellFor, 'materials'>, UpsellData> & {
  materials: (material: Material) => UpsellData
}

export const useUpsell = (): UpsellReturnType => {
  const { formatMessage } = useIntl()
  const {
    data: {
      usePaidVersion,
      plan,
      institution,
      membersCount,
      formsCount,
      entitlements,
      hasEmployees,
    },
  } = useInstitution({
    // Should be better separated
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const {
    isAllowedE2EE,
    isAllowedSSO,
    isAllowedWebhooks,
    isAllowedWhitelabel,
    isAllowedApi,
    isAllowedMultipleReportSources,
  } = useAccessRights()
  const { isPartnerAdministration } = usePartner()
  const { language } = useLocalization()

  const plans = [Plan.Unregistered, Plan.Free, Plan.Basic, Plan.Standard, Plan.Premium]
  const higherPlan = plan ? plans[plans.indexOf(plan) + 1] : undefined
  const highestPlan = institution === Institution.Company ? Plan.Premium : Plan.Standard

  const isEntitlementEnabled = (feature: Feature) =>
    entitlements.find(entitlement => entitlement.id === feature)?.isEnabled ?? false

  const membersEntitlement = entitlements?.find(
    entitlement => entitlement.id === Feature.TeamMembers
  )
  const canAddMoreUsers =
    membersEntitlement?.isEnabled && canAddMoreMembers(membersEntitlement?.value, membersCount)

  const reportingFormsEntitlement = entitlements?.find(
    entitlement => entitlement.id === Feature.ReportingForms
  )
  const canAddMoreForms =
    reportingFormsEntitlement?.isEnabled &&
    canAddMoreReportingForms(reportingFormsEntitlement?.value, formsCount)

  const showCzechImage = [Language.Cs, Language.Sk].includes(language)

  return {
    reports: {
      name: 'reports',
      icon: ulInbox01,
      title: formatMessage(messages.reports.title),
      features: formatMessage(messages.reports.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.reports.description),
      image: imageReportsCs,
      upgradeTo: Plan.Standard,
      showUpsell: !isPartnerAdministration && !usePaidVersion,
    },
    statistics: {
      name: 'statistics',
      icon: ulPieChart01,
      title: formatMessage(messages.statistics.title),
      features: formatMessage(messages.statistics.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.statistics.description),
      image: showCzechImage ? imageStatisticsCs : imageStatisticsEn,
      upgradeTo: Plan.Standard,
      showUpsell: !isPartnerAdministration && !usePaidVersion,
    },
    organisationStructure: {
      name: 'organisationStructure',
      icon: ulSettings01,
      title: formatMessage(messages.organisationStructure.title),
      features: formatMessage(messages.organisationStructure.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.organisationStructure.description),
      image: showCzechImage ? imageOrganizationStructureCs : imageOrganizationStructureEn,
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    categories: {
      name: 'categories',
      icon: ucMessageSettings,
      title: formatMessage(messages.categories.title),
      features: formatMessage(messages.categories.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.categories.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    caseStatuses: {
      name: 'caseStatuses',
      icon: ucMessageSettings,
      title: formatMessage(messages.caseStatuses.title),
      features: formatMessage(messages.caseStatuses.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.caseStatuses.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    formAppearance: {
      name: 'formAppearance',
      icon: ucMessageSettings,
      title: formatMessage(messages.formAppearance.title),
      features: formatMessage(messages.formAppearance.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.formAppearance.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    addChannelLiveHotline: {
      name: 'addChannelLiveHotline',
      icon: ulPhoneOutgoing02,
      title: formatMessage(messages.addChannelLiveHotline.title),
      features: formatMessage(messages.addChannelLiveHotline.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelLiveHotline.description),
      upgradeTo: Plan.Standard,
      // We don't want to show upsell yet, prepared for future
      showUpsell: false,
    },
    addChannelAiHotline: {
      name: 'addChannelAiHotline',
      icon: ulPhoneOutgoing02,
      title: formatMessage(messages.addChannelAiHotline.title),
      features: formatMessage(messages.addChannelAiHotline.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelAiHotline.description),
      upgradeTo: Plan.Standard,
      // We don't want to show upsell yet, prepared for future
      showUpsell: false,
    },
    addChannelAutomatedHotline: {
      name: 'addChannelAutomatedHotline',
      icon: ulPhoneCall01,
      title: formatMessage(messages.addChannelAutomatedHotline.title),
      features: formatMessage(messages.addChannelAutomatedHotline.features)
        .split(`\n`)
        .filter(Boolean),
      description: formatMessage(messages.addChannelAutomatedHotline.description),
      upgradeTo: Plan.Standard,
      showUpsell: !isAllowedMultipleReportSources,
    },
    addChannelForm: {
      name: 'addChannelForm',
      icon: ulFile02,
      title: formatMessage(messages.addChannelForm.title),
      features: formatMessage(messages.addChannelForm.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addChannelForm.description),
      upgradeTo: higherPlan ?? highestPlan,
      showUpsell: !canAddMoreForms,
    },
    addMember: {
      name: 'addMember',
      icon: ulSettings01,
      title: formatMessage(messages.addMember.title),
      features: formatMessage(messages.addMember.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.addMember.description),
      upgradeTo: higherPlan ?? highestPlan,
      showUpsell: !canAddMoreUsers,
    },
    automaticDueDate: {
      name: 'automaticDueDate',
      icon: ulSettings01,
      title: formatMessage(messages.automaticDueDate.title),
      features: formatMessage(messages.automaticDueDate.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.automaticDueDate.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    endToEndEncryption: {
      name: 'endToEndEncryption',
      icon: ulSettings01,
      title: formatMessage(messages.endToEndEncryption.title),
      features: formatMessage(messages.endToEndEncryption.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.endToEndEncryption.description),
      upgradeTo: Plan.Standard,
      showUpsell: !isAllowedE2EE,
    },
    schoolEnableAccessKey: {
      name: 'schoolEnableAccessKey',
      icon: ucMessageSettings,
      title: formatMessage(messages.schoolEnableAccessKey.title),
      features: formatMessage(messages.schoolEnableAccessKey.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.schoolEnableAccessKey.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    materials: material => ({
      name: 'materials',
      icon: ulFolder,
      title: formatMessage(messages.materials.title),
      features: formatMessage(messages.materials.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.materials.description),
      upgradeTo: Plan.Standard,
      showUpsell: !material.downloadLink || !material.isAccessible,
    }),
    sso: {
      name: 'sso',
      icon: ulSettings01,
      title: formatMessage(messages.sso.title),
      features: formatMessage(messages.sso.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.sso.description),
      upgradeTo: Plan.Premium,
      showUpsell: !isAllowedSSO,
    },
    webhooks: {
      name: 'webhooks',
      icon: ulZap,
      title: formatMessage(messages.webhooks.title),
      features: formatMessage(messages.webhooks.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.webhooks.description),
      upgradeTo: Plan.Standard,
      showUpsell: !isAllowedWebhooks,
    },
    apiKeys: {
      name: 'apiKeys',
      icon: ulZap,
      title: formatMessage(messages.apiKeys.title),
      features: formatMessage(messages.apiKeys.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.apiKeys.description),
      upgradeTo: Plan.Standard,
      showUpsell: !isAllowedApi,
    },
    whitelabel: {
      name: 'whitelabel',
      icon: ulBrush01,
      title: formatMessage(messages.whitelabel.title),
      features: formatMessage(messages.whitelabel.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.whitelabel.description),
      upgradeTo: Plan.Premium,
      image: showCzechImage ? imageWhitelabelCs : imageWhitelabelEn,
      showUpsell: !isAllowedWhitelabel,
    },
    schoolEditUserPermissions: {
      name: 'schoolEditUserPermissions',
      icon: ulFolder,
      title: formatMessage(messages.schoolEditUserPermissions.title),
      features: formatMessage(messages.schoolEditUserPermissions.features)
        .split(`\n`)
        .filter(Boolean),
      description: formatMessage(messages.schoolEditUserPermissions.description),
      upgradeTo: Plan.Standard,
      showUpsell: institution === Institution.School && !usePaidVersion,
    },
    integrations: {
      name: 'integrations',
      icon: ucLinking,
      title: formatMessage(messages.integrations.title),
      features: formatMessage(messages.integrations.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.integrations.description),
      upgradeTo: Plan.Standard,
      showUpsell: !usePaidVersion,
    },
    surveys: {
      name: 'surveys',
      icon: ucClipboard,
      title: formatMessage(messages.surveys.title),
      features: formatMessage(messages.surveys.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.surveys.description),
      upgradeTo: Plan.Standard,
      image: showCzechImage ? imageSurveysCs : imageSurveysEn,
      showUpsell: !isEntitlementEnabled(Feature.Surveys),
    },
    investigations: {
      name: 'investigations',
      icon: ulSearchRefraction,
      title: formatMessage(messages.investigations.title),
      features: formatMessage(messages.investigations.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.investigations.description),
      upgradeTo: Plan.Standard,
      image: showCzechImage ? imageInvestigationsCs : imageInvestigationsEn,
      showUpsell: !isEntitlementEnabled(Feature.Investigations),
    },
    ipAllowlist: {
      name: 'ipAllowlist',
      icon: ulServer05,
      title: formatMessage(messages.ipAllowlist.title),
      features: formatMessage(messages.ipAllowlist.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.ipAllowlist.description),
      upgradeTo: Plan.Premium,
      showUpsell: !isPartnerAdministration && !isEntitlementEnabled(Feature.IpAllowlist),
    },
    employeeManagement: {
      name: 'employeeManagement',
      icon: ulUsers02,
      title: formatMessage(messages.employeeManagement.title),
      features: formatMessage(messages.employeeManagement.features).split(`\n`).filter(Boolean),
      description: formatMessage(messages.employeeManagement.description),
      upgradeTo: Plan.Standard,
      showUpsell: !isEntitlementEnabled(Feature.EmployeeManagement) && !hasEmployees,
      image: showCzechImage ? imageEmployeeManagementCs : imageEmployeeManagementEn,
    },
  }
}
